import React, { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { EquiposService } from '../../services/equiposService';
import { Equipo, TipoEquipo, EstadoEquipo } from '../../types';
import { LoadingSpinner } from '../common/LoadingSpinner';
import toast from 'react-hot-toast';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Wrench, 
  Calendar, 
  MapPin, 
  Tag,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  DollarSign
} from 'lucide-react';

interface EquiposClienteProps {
  clienteId: string;
  clienteNombre: string;
  readOnly?: boolean;
}

const schema = yup.object().shape({
  tipo: yup.string().required('Tipo de equipo requerido'),
  marca: yup.string().optional(),
  modelo: yup.string().optional(),
  fechaInstalacion: yup.date().required('Fecha de instalación requerida'),
  observaciones: yup.string().optional()
});

type EquipoFormData = yup.InferType<typeof schema>;

export const EquiposCliente: React.FC<EquiposClienteProps> = ({ clienteId, clienteNombre, readOnly = false }) => {
  const [equipos, setEquipos] = useState<Equipo[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingEquipos, setLoadingEquipos] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEquipo, setEditingEquipo] = useState<Equipo | null>(null);
  const [showRetiroModal, setShowRetiroModal] = useState<Equipo | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue
  } = useForm<EquipoFormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      fechaInstalacion: new Date()
    }
  });

  // Cargar equipos del cliente
  const loadEquipos = useCallback(async () => {
    try {
      setLoadingEquipos(true);
      const equiposData = await EquiposService.getEquiposPorCliente(clienteId);
      setEquipos(equiposData);
    } catch (error) {
      console.error('Error al cargar equipos:', error);
      toast.error('Error al cargar equipos');
    } finally {
      setLoadingEquipos(false);
    }
  }, [clienteId]);

  useEffect(() => {
    loadEquipos();
  }, [clienteId, loadEquipos]);

  const onSubmit = async (data: EquipoFormData) => {
    if (loading) return; // Prevenir doble envío
    setLoading(true);
    try {
      const equipoData = {
        clienteId,
        tipo: data.tipo as TipoEquipo,
        marca: data.marca || 'No especificada',
        modelo: data.modelo || 'No especificado',
        numeroSerie: `AUTO-${Date.now()}`, // Generar número de serie automático
        fechaInstalacion: data.fechaInstalacion,
        estado: EstadoEquipo.ACTIVO, // Siempre activo por defecto
        ubicacion: 'Instalado', // Ubicación por defecto
        observaciones: data.observaciones,
        createdBy: 'usuario', // Se establecerá en el servicio
        updatedBy: 'usuario'  // Se establecerá en el servicio
      };

      if (editingEquipo) {
        await EquiposService.actualizarEquipo(editingEquipo.id, equipoData);
        toast.success('Equipo actualizado correctamente');
      } else {
        await EquiposService.crearEquipo(equipoData);
        toast.success('Equipo creado correctamente');
      }

      reset();
      setShowForm(false);
      setEditingEquipo(null);
      loadEquipos();
    } catch (error) {
      console.error('Error al guardar equipo:', error);
      toast.error('Error al guardar equipo');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (equipo: Equipo) => {
    setEditingEquipo(equipo);
    setValue('tipo', equipo.tipo);
    setValue('marca', equipo.marca);
    setValue('modelo', equipo.modelo);
    setValue('fechaInstalacion', equipo.fechaInstalacion || new Date());
    setValue('observaciones', equipo.observaciones || '');
    setShowForm(true);
  };

  const handleRetirar = async (equipo: Equipo, motivo: string, observaciones?: string) => {
    try {
      setLoading(true);
      await EquiposService.retirarEquipo(equipo.id, motivo, observaciones);
      toast.success('Equipo retirado correctamente');
      setShowRetiroModal(null);
      loadEquipos();
    } catch (error) {
      console.error('Error al retirar equipo:', error);
      toast.error('Error al retirar equipo');
    } finally {
      setLoading(false);
    }
  };

  const getEstadoIcon = (estado: EstadoEquipo) => {
    switch (estado) {
      case EstadoEquipo.ACTIVO:
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case EstadoEquipo.MANTENIMIENTO:
        return <Wrench className="h-4 w-4 text-yellow-500" />;
      case EstadoEquipo.RETIRADO:
        return <XCircle className="h-4 w-4 text-red-500" />;
      case EstadoEquipo.REPARACION:
        return <AlertTriangle className="h-4 w-4 text-orange-500" />;
      case EstadoEquipo.GARANTIA:
        return <Tag className="h-4 w-4 text-blue-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getTipoEquipoLabel = (tipo: TipoEquipo) => {
    const labels = {
      [TipoEquipo.DISPENSER_NORMAL]: 'Dispenser Normal',
      [TipoEquipo.DISPENSER_FRIO_CALOR]: 'Dispenser Frío/Calor',
      [TipoEquipo.MAQUINA_AGUA]: 'Máquina de Agua',
      [TipoEquipo.PURIFICADOR]: 'Purificador',
      [TipoEquipo.ENFRIADOR]: 'Enfriador',
      [TipoEquipo.OTRO]: 'Otro'
    };
    return labels[tipo] || tipo;
  };


  if (loadingEquipos) {
    return (
      <div className="flex items-center justify-center h-32">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            Equipos de {clienteNombre}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {equipos.length} equipo{equipos.length !== 1 ? 's' : ''} registrado{equipos.length !== 1 ? 's' : ''}
          </p>
        </div>
        {!readOnly && (
          <button
            type="button"
            onClick={() => {
              reset();
              setEditingEquipo(null);
              setShowForm(true);
            }}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="h-4 w-4 mr-2" />
            Agregar Equipo
          </button>
        )}
      </div>

      {/* Lista de Equipos */}
      {!loadingEquipos && equipos.length === 0 ? (
        <div className="text-center py-8">
          <Wrench className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 dark:text-gray-400">
            No hay equipos registrados para este cliente
          </p>
        </div>
      ) : !loadingEquipos && (
        <div className="grid gap-4">
          {equipos.map((equipo) => (
            <div
              key={equipo.id}
              className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    {getEstadoIcon(equipo.estado)}
                    <h4 className="font-medium text-gray-900 dark:text-white">
                      {equipo.marca} {equipo.modelo}
                    </h4>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      ({getTipoEquipoLabel(equipo.tipo)})
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600 dark:text-gray-400">
                    <div className="flex items-center gap-1">
                      <Tag className="h-3 w-3" />
                      <span>Serie: {equipo.numeroSerie}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      <span>Instalado: {equipo.fechaInstalacion?.toLocaleDateString() || 'Fecha desconocida'}</span>
                    </div>
                    {equipo.ubicacion && (
                      <div className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        <span>{equipo.ubicacion}</span>
                      </div>
                    )}
                    {equipo.costoInstalacion && (
                      <div className="flex items-center gap-1">
                        <DollarSign className="h-3 w-3" />
                        <span>Costo: ${equipo.costoInstalacion.toLocaleString()}</span>
                      </div>
                    )}
                  </div>

                  {equipo.observaciones && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                      {equipo.observaciones}
                    </p>
                  )}

                  {equipo.garantiaHasta && equipo.garantiaHasta > new Date() && (
                    <div className="mt-2">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                        <Tag className="h-3 w-3 mr-1" />
                        Garantía hasta {equipo.garantiaHasta.toLocaleDateString()}
                      </span>
                    </div>
                  )}
                </div>

                {!readOnly && (
                  <div className="flex items-center gap-2 ml-4">
                    <button
                      type="button"
                      onClick={() => handleEdit(equipo)}
                      className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                      title="Editar equipo"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    {equipo.estado !== EstadoEquipo.RETIRADO && (
                      <button
                        type="button"
                        onClick={() => setShowRetiroModal(equipo)}
                        className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                        title="Retirar equipo"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Formulario de Equipo */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                {editingEquipo ? 'Editar Equipo' : 'Nuevo Equipo'}
              </h3>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Tipo de Equipo *
                    </label>
                    <select
                      {...register('tipo')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    >
                      <option value="">Seleccionar tipo</option>
                      <option value={TipoEquipo.DISPENSER_NORMAL}>Dispenser Normal</option>
                      <option value={TipoEquipo.DISPENSER_FRIO_CALOR}>Dispenser Frío/Calor</option>
                      <option value={TipoEquipo.MAQUINA_AGUA}>Máquina de Agua</option>
                      <option value={TipoEquipo.PURIFICADOR}>Purificador</option>
                      <option value={TipoEquipo.ENFRIADOR}>Enfriador</option>
                      <option value={TipoEquipo.OTRO}>Otro</option>
                    </select>
                    {errors.tipo && (
                      <p className="mt-1 text-sm text-red-600">{errors.tipo.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Fecha de Instalación *
                    </label>
                    <input
                      {...register('fechaInstalacion')}
                      type="date"
                      min="2020-01-01"
                      max="2030-12-31"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:[color-scheme:dark]"
                      style={{
                        colorScheme: 'dark'
                      }}
                    />
                    {errors.fechaInstalacion && (
                      <p className="mt-1 text-sm text-red-600">{errors.fechaInstalacion.message}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Marca
                    </label>
                    <input
                      {...register('marca')}
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      placeholder="Ej: Oster, Hamilton Beach (opcional)"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Modelo
                    </label>
                    <input
                      {...register('modelo')}
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      placeholder="Ej: CD-100, HB-200 (opcional)"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Observaciones
                  </label>
                  <textarea
                    {...register('observaciones')}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white resize-none"
                    placeholder="Observaciones adicionales (opcional)..."
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowForm(false);
                      setEditingEquipo(null);
                      reset();
                    }}
                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors dark:bg-gray-600 dark:text-gray-300 dark:hover:bg-gray-500"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                  >
                    {loading ? <LoadingSpinner size="sm" /> : (editingEquipo ? 'Actualizar' : 'Crear')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Retiro */}
      {showRetiroModal && (
        <RetiroEquipoModal
          equipo={showRetiroModal}
          onConfirm={handleRetirar}
          onCancel={() => setShowRetiroModal(null)}
        />
      )}
    </div>
  );
};

// Componente para el modal de retiro
interface RetiroEquipoModalProps {
  equipo: Equipo;
  onConfirm: (equipo: Equipo, motivo: string, observaciones?: string) => void;
  onCancel: () => void;
}

const RetiroEquipoModal: React.FC<RetiroEquipoModalProps> = ({ equipo, onConfirm, onCancel }) => {
  const [motivo, setMotivo] = useState('');
  const [observaciones, setObservaciones] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (motivo.trim()) {
      onConfirm(equipo, motivo.trim(), observaciones.trim() || undefined);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full">
        <div className="p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            Retirar Equipo
          </h3>
          
          <div className="mb-4">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
              Equipo: <span className="font-medium">{equipo.marca} {equipo.modelo}</span>
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Serie: <span className="font-medium">{equipo.numeroSerie}</span>
            </p>
          </div>

          <form onSubmit={(e) => { e.preventDefault(); handleSubmit(e); }} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Motivo del Retiro *
              </label>
              <input
                type="text"
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                placeholder="Ej: Fin de contrato, Equipo defectuoso"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Observaciones
              </label>
              <textarea
                value={observaciones}
                onChange={(e) => setObservaciones(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white resize-none"
                placeholder="Observaciones adicionales..."
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors dark:bg-gray-600 dark:text-gray-300 dark:hover:bg-gray-500"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Retirar Equipo
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
